// O Mestre Achado nas Finanças: a dica do momento (sem IA) e a conversa com a IA do próprio usuário.
import { useRef, useState } from 'react';
import { ActivityIndicator, KeyboardAvoidingView, Platform, Pressable, ScrollView, Text, View } from 'react-native';
import type { Financas } from '../../store/types';
import { juntarMestre, limparMestre, mensagensMestre } from '../../store/financas';
import { MESTRE, dicaDoMestre, perguntarAoMestre } from '../../lib/mestre';
import { useIAPronta } from '../../lib/ia';
import { Field, Sheet } from '../ui';
import { C, alpha } from '../theme';
import { N_, t } from '../../lib/i18n';
import { nomeCat } from './cats';
import { useCaixas } from '../../lib/caixas';

const SUGESTOES = [N_('Onde eu mais gastei este mês?'), N_('Dá para comprar algo de 3 mil em 10 vezes?'), N_('Quanto preciso guardar por mês para as minhas metas?'),
  N_('Como estou comparado ao mês passado?'), N_('Quais contas vencem nos próximos dias?')];

export function MestreCard({ d, cor }: { d: Financas; cor: string }) {
  const [aberto, setAberto] = useState(false);
  const temIA = useIAPronta();
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  return (<>
    <Pressable onPress={() => setAberto(true)} style={{ backgroundColor: alpha(cor, 0.12), borderRadius: cx.raio(16), ...cx.pads(12), flexDirection: 'row', gap: 10, alignItems: 'flex-start' }}>
      <Text style={{ fontSize: cx.icone(30) }}>{MESTRE}</Text>
      <View style={{ flex: 1, gap: 4 }}>
        <Text style={{ color: cor, fontWeight: '800', fontSize: 12, letterSpacing: 0.6 }}>{t('DICA DO MESTRE ACHADO')}</Text>
        <Text style={{ color: C.text, fontSize: 14 }}>{dicaDoMestre(d, nomeCat)}</Text>
        <Text style={{ color: cor, fontWeight: '700', fontSize: 13 }}>{temIA ? '💬 ' + t('Perguntar ao Mestre') : '💬 ' + t('Conversar com o Mestre (precisa da sua IA)')}</Text>
      </View>
    </Pressable>
    <ConversaMestre d={d} aberta={aberto} fechar={() => setAberto(false)} cor={cor} temIA={temIA} />
  </>);
}

function ConversaMestre({ d, aberta, fechar, cor, temIA }: { d: Financas; aberta: boolean; fechar: () => void; cor: string; temIA: boolean }) {
  const [txt, setTxt] = useState('');
  const [pensando, setPensando] = useState(false);
  const [erro, setErro] = useState('');
  const rolar = useRef<ScrollView>(null);
  const msgs = mensagensMestre(d);
  async function enviar(pergunta: string) {
    const p = pergunta.trim();
    if (!p || pensando) return;
    setTxt(''); setErro(''); setPensando(true);
    const eu = { de: 'eu' as const, texto: p, em: new Date().toISOString() };
    juntarMestre([eu]);
    try {
      const r = await perguntarAoMestre(d, [...msgs, eu], p, nomeCat);
      juntarMestre([{ de: 'mestre', texto: r, em: new Date().toISOString() }]);
    } catch (e: any) { setErro(String(e?.message ?? e)); }
    finally { setPensando(false); setTimeout(() => rolar.current?.scrollToEnd({ animated: true }), 100); }
  }
  return (
    <Sheet visible={aberta} onClose={fechar} title={`${MESTRE} ${t('Mestre Achado')}`}
      right={msgs.length ? <Pressable onPress={limparMestre}><Text style={{ color: C.sub, fontSize: 13 }}>{t('Limpar')}</Text></Pressable> : undefined}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView ref={rolar} contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 20 }} onContentSizeChange={() => rolar.current?.scrollToEnd({ animated: false })}>
          <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 4 }}>
            <Text style={{ color: C.text }}>{t('Olá! Eu sou o Mestre Achado. Pergunte sobre os seus gastos, contas, cartões e metas: eu respondo com os seus números.')}</Text>
            <Text style={{ color: C.mute, fontSize: 11 }}>{t('Só os totais das Finanças vão para a sua IA. Não é recomendação de investimento.')}</Text>
          </View>
          {!temIA && <Text style={{ color: C.warn }}>{t('Para conversar, ligue a sua IA em Ajustes › Inteligência artificial (Claude, ChatGPT ou Gemini).')}</Text>}
          {msgs.map((m, i) => (
            <View key={i} style={{ alignSelf: m.de === 'eu' ? 'flex-end' : 'flex-start', maxWidth: '88%', backgroundColor: m.de === 'eu' ? cor : C.card, borderRadius: 14, padding: 10 }}>
              <Text style={{ color: m.de === 'eu' ? '#fff' : C.text }}>{m.texto}</Text>
            </View>
          ))}
          {pensando && <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}><ActivityIndicator color={cor} /><Text style={{ color: C.sub }}>{t('O Mestre está pensando...')}</Text></View>}
          {!!erro && <Text style={{ color: C.danger }}>{erro}</Text>}
          {temIA && !msgs.length && SUGESTOES.map(s => (
            <Pressable key={s} onPress={() => enviar(t(s))} style={{ borderWidth: 1, borderColor: alpha(cor, 0.5), borderRadius: 12, padding: 10 }}>
              <Text style={{ color: cor }}>{t(s)}</Text>
            </Pressable>
          ))}
        </ScrollView>
        {temIA && <View style={{ flexDirection: 'row', gap: 8, padding: 12, borderTopWidth: 1, borderTopColor: C.line, alignItems: 'center' }}>
          <View style={{ flex: 1 }}><Field value={txt} onChangeText={setTxt} placeholder={t('Pergunte ao Mestre...')} onSubmitEditing={() => enviar(txt)} returnKeyType="send" /></View>
          <Pressable onPress={() => enviar(txt)} disabled={pensando} style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: cor, alignItems: 'center', justifyContent: 'center', opacity: pensando ? 0.5 : 1 }}>
            <Text style={{ color: '#fff', fontSize: 18 }}>➤</Text>
          </Pressable>
        </View>}
      </KeyboardAvoidingView>
    </Sheet>
  );
}
